/*
  FAQ content for the Help page search.

  Each entry is plain data so it stays searchable — FaqSearch flattens
  question + answer + steps + keywords into one haystack. Anything you want a
  user to be able to *find* by typing must live in one of those fields, not in
  markup.

  Keep `keywords` for words people actually type that don't appear in the
  answer ("password", "can't log in", "truck") — that's what makes the search
  forgiving.

  Categories must be listed in `faqCategories` below to get a filter chip.
*/

export const faqs = [
  // ---------------------------------------------------------------- Getting started
  {
    id: "why-node",
    category: "Getting Started",
    question: "Why is it called Node?",
    answer: [
      "A node is a point where separate paths meet — which is exactly what this site is meant to be for Big C employees. Orders, quotes, web leads, forms, lead times, and forklift check-ins used to live in different places; Node is the central intersection where they all come together.",
      "That's the official answer. The unofficial one is that Brandon liked the name.",
    ],
    keywords: [
      "name",
      "called",
      "meaning",
      "origin",
      "named",
      "brandon",
      "what does node mean",
      "weird name",
    ],
  },
  {
    id: "find-a-page",
    category: "Getting Started",
    question: "How do I find a page in Node?",
    answer: [
      "Almost everything lives behind the menu button in the top-left corner of every page. The menu is grouped by area — Forms, Windows & Doors, Sales & Leads, SBCF, Everyday Tools, and more — and each group expands to show the pages inside it.",
      "Not sure which group a page is in? Type into the “Find a page…” box at the top of the menu — it searches every page you can open and filters the list as you type.",
      "Or skip the menu: on any page, click the “Search pages…” box in the middle of the top bar (the magnifying glass on a phone), or press Ctrl+K (⌘K on a Mac). Start typing a page's name (or what it's for, like “lead times”), then use the arrow keys and Enter — or click a result — to jump straight there. If nothing matches, the last option searches the Help & FAQ for the same words.",
    ],
    steps: [
      "Click the menu icon in the top-left corner (or press Ctrl+K / ⌘K to search instead).",
      "Type the page's name into “Find a page…”, or click a section to expand it.",
      "Click the page name to open it.",
    ],
    keywords: [
      "navigate",
      "menu",
      "sidebar",
      "hamburger",
      "where is",
      "lost",
      "search",
      "find a page",
      "ctrl k",
      "cmd k",
      "keyboard shortcut",
      "quick search",
    ],
  },
  {
    id: "missing-menu-item",
    category: "Getting Started",
    question: "Why can't I see a page or menu section that my coworker has?",
    answer: [
      "Node hides pages you don't have a role for, so two people can see very different menus. A menu section only appears when it holds at least one page your roles allow, which is why sections like Cabinets, Fleet, and Admin are missing entirely for most people.",
      "You can see your own roles on your Profile page. If a role is missing, HelpDesk has to add it — it isn't something you can change yourself. They can also give you access to a single page without adding a whole role.",
      "Once access is added, the page shows up in your menu on its own within about 20 seconds (or as soon as you click back into the Node tab). You don't need to sign out or refresh, and you'll get a notification.",
    ],
    link: { label: "Check my roles", to: "/profile" },
    keywords: [
      "permission",
      "access denied",
      "hidden",
      "role",
      "can't see",
      "missing",
      "sign out",
      "log out",
      "refresh",
    ],
  },
  {
    id: "access-denied",
    category: "Getting Started",
    question: "I opened a link and it says I don't have access to this page.",
    answer: [
      "That message means your account doesn't carry a role the page requires and hasn't been given access to that page. It shows up even when someone sends you a direct link, because Node checks access on the page itself, not just in the menu.",
      "Email HelpDesk with the link you were trying to open. They can add the role it needs or give you access to just that page. Once they do, the page opens on its own within about 20 seconds — you don't need to sign out.",
    ],
    note: "Copy the full web address out of your browser's address bar into the email — that's what tells HelpDesk exactly which role to add.",
    keywords: ["denied", "forbidden", "blocked", "no access", "restricted"],
  },
  {
    id: "pin-a-page",
    category: "Getting Started",
    question:
      "Can I put the pages I use every day on my home page? (Favorites)",
    answer: [
      "Yes. Star the pages you use most and they become cards under Quick access on the home page, right next to the defaults everyone gets (File Share, Calendar, and Help & FAQ).",
      "The list you pick from mirrors the navigation menu and only offers pages you can open.",
    ],
    steps: [
      "Go to the home page.",
      "Click Edit next to Quick access.",
      "Star the pages you want (click a star again to remove it).",
      "Close the dialog — your starred pages now show as Quick access cards.",
    ],
    link: { label: "Go to the home page", to: "/" },
    note: "Favorites are saved in this browser on this computer — they don't follow you to another machine yet.",
    keywords: [
      "favorite",
      "bookmark",
      "shortcut",
      "quick link",
      "star",
      "pin",
      "pinned pages",
      "home screen",
      "dashboard",
      "widget",
    ],
  },
  {
    id: "widgets",
    category: "Getting Started",
    question: "Can I see live summaries of pages on my home page? (Widgets)",
    answer: [
      "Yes. The home page has a My widgets section — pick the pages you care about and each one becomes a tile you see the moment you sign in.",
      "Most pages are marked Live in the picker — their tiles show real data, like counts of what's waiting on you, recent items, or a freshness date. Pages that are just a form or a tool become a shortcut tile that opens them in one click.",
      "The picker only offers pages you can open, and clicking a widget's title always takes you to the full page.",
    ],
    steps: [
      "Go to the home page.",
      "Click Edit next to My widgets.",
      "Click a page to add its widget (click again to remove it).",
      "Close the dialog — your widgets now show under My widgets.",
    ],
    link: { label: "Go to the home page", to: "/" },
    note: "Like favorites, widgets are saved in this browser on this computer — they don't follow you to another machine yet.",
    keywords: [
      "widget",
      "tile",
      "summary",
      "dashboard",
      "live",
      "at a glance",
      "counts",
      "home screen",
    ],
  },
  {
    id: "notifications",
    category: "Getting Started",
    question: "How do notifications work in Node?",
    answer: [
      "The bell at the top-right of every page shows a red badge with your unread count. Click it to read your notifications — each one shows who it's from, and an Open link jumps straight to the page it's about when there is one. New notifications also appear at the top of the home page until you clear them.",
      "Node notifies you when something needs you or your work moved along: a web lead or window RFQ is assigned to you, a quote is ready for your review or gets approved or declined, a spec sheet or cabinet quote you're on changes, someone replies to your feature request, or a coworker catches you in the act.",
      "Click the X on a notification to mark it read, or use Mark all as read. The list refreshes on its own about once a minute.",
    ],
    note: "Marking a notification read removes it from the bell — there's no history view of past notifications.",
    keywords: [
      "bell",
      "alert",
      "badge",
      "unread",
      "notify",
      "red dot",
      "mark as read",
      "assigned",
    ],
  },
  {
    id: "dark-mode",
    category: "Getting Started",
    question: "How do I turn on dark mode or change how Node looks?",
    answer: [
      "Click the sun (or moon) icon in the top-right corner of any page, next to the notification bell. That opens Appearance, which has two settings that work together.",
      "Appearance picks light or dark: Light, Dark, or Auto. Auto follows your computer's or phone's own light/dark setting and switches with it — choose Light or Dark if you want Node to stay one way no matter what your computer does.",
      "Style picks the look: Liquid Glass (the default — translucent, layered panels over a soft color background, like Apple's newest look) or Classic (solid, flat panels). Both work in light and dark. If you find the see-through panels distracting, Classic is the one to pick.",
      "Changes apply immediately and are saved on this browser, so a different computer or browser keeps its own setting.",
    ],
    steps: [
      "Click the sun/moon icon in the top-right corner.",
      "Under Appearance, choose Light, Dark, or Auto.",
      "Under Style, choose Liquid Glass or Classic.",
    ],
    note: "If your computer has “Reduce transparency” turned on (Mac, iPhone, or Windows accessibility settings), Liquid Glass panels show as solid automatically.",
    keywords: [
      "dark",
      "light",
      "theme",
      "night mode",
      "bright",
      "eyes",
      "appearance",
      "liquid glass",
      "glass",
      "classic",
      "transparent",
      "see through",
      "blurry",
      "auto",
      "system",
      "look",
      "colors",
    ],
  },

  // ---------------------------------------------------------------- Account & access
  {
    id: "install-node-mobile",
    category: "Account & Access",
    question: "How do I get the Node Mobile app on my iPhone?",
    answer: [
      "Node Mobile, the Big C iPhone app, isn't in the public App Store search. It installs with a one-time download code instead.",
      "Ask IT for a code. They'll text it to your phone or email it to you as a link. Open the link on your iPhone or iPad to redeem the code in the App Store and install the app.",
    ],
    note: "Each code works once. If yours says it's already been used, ask IT to send you a new one.",
    keywords: [
      "mobile app",
      "phone app",
      "iphone app",
      "ipad",
      "download",
      "install",
      "app store",
      "redeem",
      "redemption code",
      "download code",
      "promo code",
    ],
  },
  {
    id: "sent-to-a-page",
    category: "Account & Access",
    question: "Why did Node suddenly switch me to a different page?",
    answer: [
      "An admin sent you there, usually to help you find something. Admins can send someone's open Node tab to a page from Currently Online or from the admin assistant in the Node Mobile app.",
      "A notice at the top of the page says who sent you. Your browser's Back button returns you to where you were. Nothing you'd typed is submitted, but a form you were partway through may need to be filled in again.",
    ],
    note: "It happens on a Node tab you have open, within about 20 seconds or as soon as you switch back to that tab. A request that isn't picked up within 10 minutes is dropped.",
    keywords: [
      "redirect",
      "redirected",
      "moved",
      "changed page",
      "jumped",
      "switched",
      "sent me",
      "took me",
      "page changed by itself",
      "admin",
    ],
  },
  {
    id: "sign-in",
    category: "Account & Access",
    question: "How do I sign in to Node? Do I need a separate password?",
    answer: [
      "No separate password. Node uses your Big C Microsoft 365 account, so you sign in with the same credentials as your work email. If you're already signed in on that browser, Node signs you in automatically. Priebe's Creative Woodworking staff sign in the same way, using their @priebeswoodworking.com account.",
      "If sign-in loops or fails, sign out completely, close the browser, and try again before contacting HelpDesk.",
      "If you see “We couldn't find your account details”, your Microsoft sign-in worked but your account isn't in the employee directory yet (common for new hires). Use the Submit a Ticket button on that screen to contact HelpDesk.",
    ],
    keywords: [
      "password",
      "login",
      "log in",
      "sign on",
      "microsoft",
      "office 365",
      "sso",
      "credentials",
      "account details",
      "account not found",
      "new hire",
      "new employee",
      "priebe",
      "priebes",
      "priebeswoodworking",
      "creative woodworking",
    ],
  },
  {
    id: "mfa-setup",
    category: "Account & Access",
    question: "A page is asking for a 6-digit code — what is that?",
    answer: [
      "Some pages require multi-factor authentication. The first time you open one, Node shows a QR code that you scan with an authenticator app (Microsoft Authenticator, Google Authenticator, or similar). After that the app generates a new 6-digit code every 30 seconds.",
      "When you verify, you can choose how long the device stays trusted: 1 Day, 3 Days, 7 Days, or 30 Days. Pick 30 Days on your own computer so you aren't prompted constantly; pick 1 Day on a shared machine.",
    ],
    steps: [
      "Scan the QR code with your authenticator app.",
      "Type the 6-digit code from the app into the box.",
      "Choose a verification length (1, 3, 7, or 30 days).",
      "Click Verify.",
    ],
    keywords: [
      "mfa",
      "2fa",
      "two factor",
      "authenticator",
      "qr code",
      "verification code",
      "otp",
    ],
  },
  {
    id: "profile-photo",
    category: "Account & Access",
    question: "How do I change my profile picture?",
    answer: [
      "Open your Profile from the avatar in the top-right corner, click the photo, choose an image, then click Save Photo. You'll see a “Photo updated!” confirmation when it saves.",
    ],
    link: { label: "Go to my profile", to: "/profile" },
    keywords: ["avatar", "picture", "headshot", "image", "photo"],
  },
  {
    id: "sign-out",
    category: "Account & Access",
    question: "How do I sign out?",
    answer: [
      "Open your Profile from the top-right avatar and use the sign-out button at the bottom of the card. This matters on shared computers — closing the tab alone leaves your session active.",
    ],
    link: { label: "Go to my profile", to: "/profile" },
    keywords: ["logout", "log out", "sign off", "shared computer", "kiosk"],
  },

  // ---------------------------------------------------------------- Forms & requests
  {
    id: "submit-a-form",
    category: "Forms & Requests",
    question: "Where do I find all the forms?",
    answer: [
      "Every form on the site lives in one place now: Submit a Form, under Forms in the menu (it's the first section). RFQs, the Remodel Form, Window Service Requests, Order Problems, Caught in the Act, and Request a Feature are all cards there — use the search box to find one fast.",
      "Each card says where to track that form after you submit it. Old links and favorites to the individual forms still work; they just aren't separate rows in the menu anymore.",
    ],
    steps: [
      "Open the menu and expand Forms (the first section).",
      "Click Submit a Form.",
      "Pick the form — or type into “Find a form…” to filter the cards.",
    ],
    link: { label: "Open Submit a Form", to: "/forms" },
    keywords: [
      "forms",
      "all forms",
      "where are the forms",
      "form list",
      "hub",
      "submit",
      "start a form",
    ],
  },
  {
    id: "my-submissions",
    category: "Forms & Requests",
    question: "Where can I see everything I've submitted?",
    answer: [
      "My Submissions, under Forms in the menu, lists everything you've sent in across every form — RFQ forms and drafts, window service requests, remodel leads, feature requests, and Caught in the Act recognitions — newest first, each with a status.",
      "Use the chips at the top to filter to one form type, or the search box to find a specific customer or request. Each row has a button to open the request, view its PDF, or continue a draft.",
    ],
    link: { label: "Open My Submissions", to: "/forms/my-submissions" },
    note: "Order Problem reports are anonymous by design, so they can't be listed here.",
    keywords: [
      "submissions",
      "history",
      "everything i submitted",
      "track",
      "status",
      "my forms",
      "my requests",
      "drafts",
    ],
  },
  {
    id: "report-order-problem",
    category: "Forms & Requests",
    question: "How do I report an Order Problem?",
    answer: [
      "Open Submit a Form (menu → Forms) and pick Order Problem. You must pick a branch and at least one problem type before the form will submit; the Sales Order number, a written explanation, and photos are optional but make the report far more useful.",
      "You can attach up to 3 photos. Reports are anonymous — your name and email are not attached, and the team can't tell who filed one.",
      "Because the report is anonymous, nobody can reply to you and you won't get a notification when it's resolved. If you need an answer back, include how to reach you in the comments or contact the branch directly.",
    ],
    steps: [
      "Open the menu, expand Forms, and click Submit a Form, then Order Problem.",
      "Select the branch.",
      "Select one or more problem types.",
      "Add the Sales Order # and a description of what happened.",
      "Attach up to 3 photos if you have them.",
      "Click Submit Report.",
    ],
    link: { label: "Report an Order Problem", to: "/forms/order-problem" },
    keywords: [
      "problem",
      "issue",
      "wrong",
      "damaged",
      "short",
      "complaint",
      "mistake",
      "report",
      "anonymous",
      "anonymously",
      "name",
    ],
  },
  {
    id: "recognize-coworker",
    category: "Forms & Requests",
    question: "How do I recognize a coworker for doing something great?",
    answer: [
      "Open Submit a Form (menu → Forms) and pick Caught in the Act. Pick the person you're recognizing, describe what they did and why it deserves recognition, and submit.",
      "Managers and Admins can browse everything that's been submitted under Caught in the Act Entries, in the menu under Everyday Tools.",
    ],
    link: { label: "Submit a recognition", to: "/forms/recognition" },
    keywords: [
      "caught in the act",
      "recognition",
      "praise",
      "kudos",
      "shout out",
      "award",
      "thank",
    ],
  },
  {
    id: "new-rfq",
    category: "Forms & Requests",
    question: "How do I start a new RFQ?",
    answer: [
      "Open Submit a Form (menu → Forms) and pick New RFQ. It's a three-step wizard: set the customer, choose whether it's a quote or an order, then pick the form you want to build. Everything you entered up front carries into the form, so you only set the customer and pricing once.",
      "On the customer step you can search Bistrack to pull in an existing account, or type the details by hand. You can't continue without at least a customer name or account number.",
    ],
    steps: [
      "Menu → Forms → Submit a Form → New RFQ.",
      "Search Bistrack for the customer, or enter them manually.",
      "Choose quote or order.",
      "Pick the form you want to build.",
    ],
    link: { label: "Start a new RFQ", to: "/forms/new/rfq" },
    keywords: [
      "rfq",
      "request for quote",
      "quote request",
      "bid",
      "pricing",
      "bistrack",
      "customer",
      "margin",
    ],
  },
  {
    id: "find-my-forms",
    category: "Forms & Requests",
    question:
      "Where do I find the forms I've already submitted, or a draft I didn't finish?",
    answer: [
      "My Submissions (menu → Forms) is the one place that lists everything you've submitted — RFQ forms and drafts, service requests, remodel leads, feature requests, and recognitions — with a status on each and filter chips per form type.",
      "RFQ drafts are picked back up in My Forms: click Continue in My Forms on the draft's row. The PDF and window views only appear once a form has actually been submitted.",
    ],
    link: { label: "View my submissions", to: "/forms/my-submissions" },
    keywords: [
      "submitted",
      "history",
      "past",
      "my forms",
      "previous",
      "track",
      "draft",
      "unfinished",
      "resume",
      "pdf",
    ],
  },
  {
    id: "interior-door-form",
    category: "Forms & Requests",
    question: "How do I submit an interior door order?",
    answer: [
      "The Interior Door form saves as you go — the “Auto-saving” badge means you won't lose work if you step away, and Save & Close lets you come back to it later.",
      "A pricing summary at the bottom totals door cost and extra stock. When you're ready, click Submit as Order and choose who to send it to; picking a name submits it immediately, so make sure the form is finished first.",
      "Print PDF becomes available once the form has been saved at least once.",
    ],
    link: { label: "Open the Interior Door form", to: "/forms/doors/interior" },
    keywords: [
      "interior door",
      "door order",
      "slab",
      "submit",
      "send to",
      "pdf",
      "autosave",
    ],
  },
  {
    id: "request-a-feature",
    category: "Forms & Requests",
    question: "Node is missing something I need — how do I ask for it?",
    answer: [
      "Open Submit a Form (menu → Forms) and pick Request a Feature. That goes to the team that builds Node and is the right place for “it would be great if Node could…” ideas.",
      "For something that's broken rather than missing, email HelpDesk instead so it gets triaged as a problem.",
    ],
    link: { label: "Request a feature", to: "/forms/feature-request" },
    keywords: [
      "feature",
      "suggestion",
      "idea",
      "improvement",
      "enhancement",
      "wish",
      "request",
    ],
  },
  {
    id: "monthly-feedback-popup",
    category: "Forms & Requests",
    question: "Why does Node ask me for feedback every month?",
    answer: [
      "Once every 30 days, a popup titled “Any feedback for Node?” asks whether there's anything you'd like added, changed, or fixed. Type whatever comes to mind and click Send feedback; it goes to the team that builds Node.",
      "If you have nothing to add, click Not now or the X. Either way, sending or dismissing it hides the popup for 30 days. If you close the tab without answering, it asks again the next time you open Node.",
      "Don't want to wait a month? Request a Feature (Submit a Form, or the lightbulb button in the bottom-right corner) takes ideas any time.",
    ],
    link: { label: "Request a feature", to: "/forms/feature-request" },
    note: "The 30 days follow your account, not your browser, so answering on one computer counts everywhere.",
    keywords: [
      "feedback",
      "popup",
      "pop up",
      "survey",
      "every month",
      "monthly",
      "check-in",
      "not now",
      "dismiss",
      "stop asking",
      "suggestion",
    ],
  },
  {
    id: "remodel-form",
    category: "Forms & Requests",
    question: "How do I send in a remodel lead?",
    answer: [
      "Open Submit a Form (menu → Forms) and pick Remodel Form when a homeowner is interested in remodel quotes — windows, entry doors, or patio doors. The address is the only required customer section; fill in whatever contact details you have.",
      "Your name and email are pre-filled as the salesperson. When you click Submit, the lead is emailed to the remodel team with a printable PDF of the form attached, and you get a copy at your salesperson email.",
      "The form saves in your browser as you type, so a page reload won't lose a half-finished lead.",
    ],
    steps: [
      "Menu → Forms → Submit a Form → Remodel Form.",
      "Enter the customer's name, phone, email, and preferred contact method.",
      "Enter the home's address — street, city, state, and ZIP are required.",
      "Check what they want a quote on (use Select All for everything).",
      "Add notes, any concerns with the home, and whether it was built before 1978.",
      "Click Submit.",
    ],
    link: { label: "Open the Remodel Form", to: "/forms/remodel" },
    note: "There's no remodel dashboard yet — the email (with the attached PDF) is how the lead reaches the remodel team. Your own leads are listed under My Submissions.",
    keywords: [
      "remodel",
      "lead",
      "quote",
      "windows",
      "entry doors",
      "patio doors",
      "homeowner",
      "1978",
    ],
  },

  // ---------------------------------------------------------------- Exterior orders
  {
    id: "see-exterior-orders",
    category: "Exterior Orders",
    question: "Where can I see my exterior orders?",
    answer: [
      "Open the menu, expand Windows & Doors, and click My Exterior Orders (under Exterior Orders). The table lists orders made up of exterior items where you're the TakenBy or the Sales Rep.",
    ],
    link: {
      label: "Open My Exterior Orders",
      to: "/windowdoor/exteriororders",
    },
    keywords: [
      "my orders",
      "view orders",
      "where",
      "list",
      "table",
      "exterior",
    ],
  },
  {
    id: "request-delivery",
    category: "Exterior Orders",
    question: "How do I request a delivery for an exterior order?",
    answer: [
      "Open My Exterior Orders under Windows & Doors, find the order, and click the truck icon in the Action column. The clipboard icon next to it shows the order's items if you need to confirm you have the right one.",
      "In the window that opens, “Earliest Available Date?” is checked by default — leave it checked to take the soonest slot. Uncheck it if you need a specific date, then pick one. Notes are optional but are the only place to say something the scheduler needs to know.",
      "Once it's sent, you'll get a “Delivery Request Sent” confirmation email with the order number in the subject line.",
    ],
    steps: [
      "Go to My Exterior Orders.",
      "Click the truck (Request Delivery) icon on the order's row.",
      "Leave “Earliest Available Date?” checked, or uncheck it and choose a date.",
      "Add notes if the delivery needs special handling.",
      "Click Request Delivery.",
    ],
    link: {
      label: "Open My Exterior Orders",
      to: "/windowdoor/exteriororders",
    },
    keywords: [
      "delivery",
      "schedule",
      "deliver",
      "truck",
      "request",
      "ship",
      "date",
    ],
  },
  {
    id: "order-not-on-table",
    category: "Exterior Orders",
    question:
      "I can't find an order on the Exterior Orders table — where is it?",
    answer: [
      "Two things filter that table. First, only orders made up of exterior items appear there at all. Second, it defaults to orders where you're the TakenBy or the Sales Rep.",
      "If someone else at your branch wrote the order, turn on the “Branch Orders” toggle to widen the table to the whole branch.",
    ],
    keywords: [
      "missing",
      "can't find",
      "not showing",
      "branch orders",
      "toggle",
      "search",
    ],
  },
  {
    id: "no-request-delivery-button",
    category: "Exterior Orders",
    question: "Why is there no Request Delivery button on my order?",
    answer: [
      "The button disappears once a delivery request already exists for that order — the row will read Delivery Request Pending or Delivery Request Accepted instead.",
      "If you see a dollar-sign icon marked “Payment Outstanding” in place of the action, the order can't be requested until the balance is handled.",
    ],
    note: "Trying to request the same order twice shows a red message telling you to contact Window Door — that's the duplicate guard, not an error on your part.",
    keywords: [
      "missing button",
      "greyed out",
      "disabled",
      "payment outstanding",
      "already requested",
      "duplicate",
    ],
  },
  {
    id: "request-status",
    category: "Exterior Orders",
    question: "How do I know if my delivery request was accepted?",
    answer: [
      "My Exterior Requests, under Windows & Doors in the menu, lists every request you've made with its current status: Pending, Accepted, or Rejected. If you asked for the earliest available date, an approved request shows the actual date as “Scheduled For: {date}” instead of just Accepted.",
      "You'll also get an email each time the window & door team accepts, schedules, or rejects a request. The order number is in every subject line, so searching your inbox for it finds all the emails about that delivery. Replying to one of them reaches the window & door team.",
    ],
    link: { label: "View my requests", to: "/windowdoor/requests" },
    keywords: [
      "status",
      "accepted",
      "approved",
      "pending",
      "rejected",
      "confirmed",
      "did it go through",
      "email",
      "order number",
      "reply",
    ],
  },
  {
    id: "request-pending-too-long",
    category: "Exterior Orders",
    question:
      "My delivery request has been Pending for a while — what can I do?",
    answer: [
      "Once a request has been pending for more than 30 minutes, a red bell icon appears on that row in My Exterior Requests. Clicking it sends a reminder straight to the delivery scheduler, and you'll get a “Reminder has been sent!” confirmation.",
      "The bell won't be there before the 30 minutes are up — that's deliberate, so the scheduler isn't pinged about requests they haven't had a chance to see yet.",
    ],
    link: { label: "View my requests", to: "/windowdoor/requests" },
    keywords: [
      "stuck",
      "waiting",
      "no response",
      "remind",
      "bell",
      "nudge",
      "slow",
    ],
  },
  {
    id: "request-rejected",
    category: "Exterior Orders",
    question: "My delivery request was rejected. What happens now?",
    answer: [
      "A rejection isn't a dead end — it usually means the date you asked for doesn't work. The window & door team usually offers one or more alternative dates, and your “Delivery Request Rejected” email lists them as buttons.",
      "Click the date that works — it opens the request in Node — then click Accept to confirm. That schedules the delivery; you don't need to submit a new request.",
      "If the email doesn't list any dates, check the team's notes in it, or reply to the email — replies go to the window & door team.",
    ],
    keywords: [
      "rejected",
      "denied",
      "declined",
      "alternative date",
      "new date",
      "reschedule",
    ],
  },

  // ---------------------------------------------------------------- Window & door
  {
    id: "lead-times",
    category: "Window & Door",
    question: "Where do I check current window and door lead times?",
    answer: [
      "Lead Times is open to everyone — find it in the menu under Windows & Doors. You can also star it on the home page (Edit next to Quick access) to keep it one click away.",
      "Each brand card shows an effective date and a Product / Was / Now table. Rows that changed in the latest update are highlighted, and item-specific notes appear in red, so you can see at a glance what moved. There's a search box if you'd rather jump straight to a product.",
      "Window Door Managers and Admins also get a “Manage lead times” button for publishing updates.",
    ],
    link: { label: "View lead times", to: "/windowdoor/lead-times" },
    keywords: [
      "lead time",
      "how long",
      "eta",
      "wait",
      "delivery time",
      "weeks",
      "availability",
      "backorder",
    ],
  },
  {
    id: "window-form-status",
    category: "Window & Door",
    question: "What do the window form statuses mean?",
    answer: [
      "A window form moves through five steps: Submitted → In Review → Quote Sent → Approved → Ordered.",
      "Three statuses sit off that track. Needs Revision means the window team wants changes from you before they can quote it. Reviewing Revisions means your changes went back to them. Cancelled means the form is closed.",
    ],
    keywords: [
      "status",
      "under review",
      "quote sent",
      "approved",
      "converted to order",
      "stage",
      "progress",
    ],
  },
  {
    id: "window-form-needs-revision",
    category: "Window & Door",
    question: "My window form says Needs Revision — what am I supposed to do?",
    answer: [
      "The window team has asked for changes. Open the form from My Window Forms and use the “Revise the RFQ” editor to correct it, then submit the revision — that moves it to Reviewing Revisions and puts it back in their queue.",
      "Once a form reaches Converted to Order it can no longer be revised; contact the window team directly at that point.",
    ],
    link: {
      label: "Open my window forms",
      to: "/windowdoor/sales/windowforms",
    },
    keywords: ["revision", "changes", "fix", "edit", "resubmit", "rejected"],
  },
  {
    id: "approve-window-quote",
    category: "Window & Door",
    question: "How do I approve or decline a window quote?",
    answer: [
      "When the window team sends a quote back, the form shows a “Quote ready for your review” banner with Approve Quote and Decline Quote buttons. Approving moves it toward Ordered; declining sends it back for another look.",
    ],
    link: {
      label: "Open my window forms",
      to: "/windowdoor/sales/windowforms",
    },
    keywords: ["approve", "accept quote", "decline", "sign off", "review"],
  },
  {
    id: "window-form-assignment",
    category: "Window & Door",
    question: "Who gets assigned my window form, and how?",
    answer: [
      "Window Door Managers triage incoming forms in the Window Quote Manager. There's a “Needs assignment” queue sorted oldest-first, and an “Assign quoter” dropdown on each row — picking a name assigns it immediately.",
      "Once assigned, the quoter works it from Assigned Window Forms.",
    ],
    keywords: [
      "assign",
      "quoter",
      "who is working",
      "unassigned",
      "queue",
      "triage",
    ],
  },

  {
    id: "service-request-submit",
    category: "Window & Door",
    question: "How do I submit a window or door service request?",
    answer: [
      "Open Submit a Form (menu → Forms) and pick Window Service Request. Fill in the customer, address, manufacturer, and a description of the issue, attach photos if you have them, and click Submit Request.",
      "Your name and email are filled in for you, and the form saves what you've typed in this browser as you go, so a reload won't lose your work.",
      "When you submit, the window/door shop is notified and the request appears under My Service Requests with a request number you can reference.",
    ],
    link: {
      label: "Start a service request",
      to: "/windowdoor/service-request",
    },
    keywords: [
      "service",
      "warranty",
      "repair",
      "broken window",
      "broken door",
      "service form",
      "window service",
      "door service",
      "submit request",
    ],
  },
  {
    id: "service-request-track",
    category: "Window & Door",
    question: "How do I check on a service request I submitted?",
    answer: [
      "Open My Submissions (menu → Forms) and use the Service Requests chip, or click through to My Service Requests. Each request shows its status, who in the shop is handling it, and how long it's been open.",
      "Open a request to see a dated progress timeline (submitted → homeowner contacted → job quoted → parts ordered → service completed, with when each step happened and what's up next) and a chat thread — messages you post there go straight to the shop, and you get a notification when they reply.",
      "Forgot a photo? Use “Add Files” on the request's Attachments card any time after submitting — the shop is notified when new files land.",
      "Want the full history? Click Activity log at the top of the request. It lists every change (checklist boxes, status, who it's assigned to, edits, files, messages) and every email sent about the request, with who did it and when. Emails that didn't go out show in red with the reason.",
    ],
    link: {
      label: "Open my service requests",
      to: "/windowdoor/my-service-requests",
    },
    keywords: [
      "status",
      "track",
      "progress",
      "where is my request",
      "service update",
      "parts",
      "technician",
      "chat",
      "message the shop",
      "add photo",
      "attach",
      "upload",
      "timeline",
      "progress",
      "activity log",
      "history",
      "who changed",
      "email",
      "did the email send",
    ],
  },
  {
    id: "service-request-manage",
    category: "Window & Door",
    question: "How does the shop work the service request queue?",
    answer: [
      "The Service Request Manager (in the menu under Windows & Doors → Service Requests; Window Door Managers and Admins only) lists every request with status counters down the side and a Technician Open Jobs panel showing who's carrying what.",
      "Assign a technician straight from the Assignee dropdown on a row, or open the request to work the checklist: ticking boxes like Job Quoted or Parts Ordered updates the status automatically and notifies the salesperson. Hold parks a request as Inactive, and Take Off closes it without completing it.",
      "Need to fix something the salesperson entered? Open the request and click Edit on the Request Details card. Every change is recorded in a change log on the card and posted into the request's chat, so both sides can see what changed and who changed it.",
      "Each request also has a Download PDF button for a printable copy — for warranty claims or taking into the field.",
      "The Reports tab shows monthly volume, top submitters, manufacturer breakdown, and average days to complete, and Export CSV downloads the queue (current filters applied) or the full history for a spreadsheet.",
      "A request came in by phone or on paper? Managers can click New Request on the dashboard to enter it manually — only the issue, a name, and a contact number are required, and the rest can be filled in later with Edit.",
      "The queue keeps your place. Status and assignee filters, the search box, sorting, and the page you're on stay put when you refresh, use the browser's Back button, or click All requests on a request. They're saved in the page's web address, so you can bookmark or share a filtered view.",
      "Every weekday at 9 AM, windowdoorservice@bigclumber.com gets an email listing every open request that nobody is assigned to, oldest first. Each one has an Open request link and a Download PDF link. If every open request has a technician, no email is sent that day.",
      "Activity log keeps the full history. On a request, it lists every change and every email about it (sent or failed), with who did it and when. On the dashboard it covers the whole queue, including whether the 9 AM email went out, and clicking a request number opens that request. Use the Failed filter to find emails that didn't send.",
    ],
    link: {
      label: "Open the Service Request Manager",
      to: "/windowdoor/service-requests",
    },
    keywords: [
      "assign",
      "technician",
      "checklist",
      "hold",
      "take off",
      "queue",
      "manage",
      "shop dashboard",
      "pdf",
      "edit",
      "change",
      "fix address",
      "wrong phone number",
      "change log",
      "report",
      "export",
      "csv",
      "excel",
      "stats",
      "manual entry",
      "phone call",
      "enter request",
      "unassigned",
      "daily email",
      "morning email",
      "summary email",
      "filters reset",
      "lost my place",
      "back button",
      "refresh",
      "activity log",
      "history",
      "audit",
      "who changed",
      "email failed",
      "did the email send",
    ],
  },

  // ---------------------------------------------------------------- Web leads
  {
    id: "weblead-assigned-to-me",
    category: "Web Leads",
    question: "How do I know when I've been assigned a web lead?",
    answer: [
      "Node sends you a notification when a lead is assigned to you — watch the bell at the top of the page — so you don't have to sit watching the queue.",
      "You can always check for yourself on the Web Leads page — the “My active leads” and “Awaiting my contact” tiles at the top show what's currently on your plate.",
    ],
    link: { label: "Open Web Leads", to: "/webleads" },
    keywords: [
      "assigned to me",
      "notified",
      "email",
      "alert",
      "new lead",
      "my leads",
    ],
  },
  {
    id: "weblead-where",
    category: "Web Leads",
    question: "Where do I view my web leads?",
    answer: [
      "Open the menu, expand Sales & Leads, and click Web Leads. Leads are split across tabs — Active is where your working leads live, and Completed holds the ones that have been closed out.",
      "There's a search box that matches name, email, phone, address, and the customer's request, plus quick filters for “Awaiting contact” and “Needs salesperson”.",
    ],
    link: { label: "Open Web Leads", to: "/webleads" },
    keywords: [
      "view",
      "where",
      "find",
      "my web leads",
      "list",
      "active",
      "completed",
      "tab",
      "search",
    ],
  },
  {
    id: "weblead-assignment",
    category: "Web Leads",
    question: "How do web leads get assigned?",
    answer: [
      "Assignment happens in two stages. A Web Lead Manager or Admin first assigns the lead to a manager, and that manager then assigns salespeople from their own team.",
      "That's why a lead can sit as “Needs salesperson” — it has a manager but nobody working it yet. Reassigning the manager clears the previous assignments and starts that lead over.",
    ],
    link: { label: "Open Web Leads", to: "/webleads" },
    keywords: [
      "assign",
      "who gets",
      "salesperson",
      "manager",
      "unassigned",
      "queue",
      "distribute",
    ],
  },
  {
    id: "weblead-visibility",
    category: "Web Leads",
    question: "Why can't I see all the web leads?",
    answer: [
      "Web leads are scoped by role. Admins, Web Lead Managers, and the Executive Team see every branch. Area Managers see their region. Everyone else — including General Managers — sees their own branch only.",
      "The Unassigned queue is the exception: it's company-wide, and it only appears for roles allowed to assign leads.",
    ],
    keywords: [
      "can't see",
      "missing leads",
      "branch",
      "region",
      "visibility",
      "filtered",
      "only mine",
    ],
  },
  {
    id: "weblead-statuses",
    category: "Web Leads",
    question: "What do the web lead status badges mean?",
    answer: [
      "The badge is worked out from the lead itself rather than set by hand, so it always reflects what's actually been recorded.",
      "Unassigned means no manager yet. Needs salesperson means a manager but no salespeople. Awaiting contact means nobody has logged a phone call or email yet. In progress means contact has been made. Quoted means a quote number is on the lead, Order placed means an order number is, and Won shows the order number once the lead is completed.",
    ],
    keywords: [
      "status",
      "badge",
      "awaiting contact",
      "in progress",
      "quoted",
      "won",
      "label",
      "color",
    ],
  },
  {
    id: "weblead-update",
    category: "Web Leads",
    question: "How do I update or complete a web lead?",
    answer: [
      "Everything happens on the lead card. Flip the Phone and Email toggles as you make contact — that's what moves the lead off “Awaiting contact”. Add a Quote # when you quote it and an Order # when it turns into an order, then click Complete.",
      "Delete asks you to confirm before it fires, and deleted leads can be restored — they aren't gone for good.",
    ],
    link: { label: "Open Web Leads", to: "/webleads" },
    keywords: [
      "update",
      "complete",
      "close",
      "finish",
      "contacted",
      "quote number",
      "order number",
      "delete",
      "restore",
    ],
  },
  {
    id: "weblead-map",
    category: "Web Leads",
    question: "Why is a lead missing from the Web Lead Map?",
    answer: [
      "Leads are only placed on the map once a manager has been assigned, so anything still sitting in the Unassigned queue has no pin. The map shows a count of leads “without a mapped address” for exactly this reason.",
      "The map itself has Pins and Density views, radius options of 10, 20, or 30 miles, and date ranges from 30 days out to 12 months.",
    ],
    link: { label: "Open the Web Lead Map", to: "/webleads/webleadmap" },
    keywords: [
      "map",
      "pin",
      "location",
      "missing",
      "geocode",
      "address",
      "heat map",
      "density",
    ],
  },

  // ---------------------------------------------------------------- Cabinet quotes
  {
    id: "cabinet-new-quote",
    category: "Cabinet Quotes",
    question: "How do I start a cabinet quote?",
    answer: [
      "Click New Quote on the Cabinet Quote Dashboard. You'll need a quote title, the customer's name, phone, and email, and the job address before you can continue — those are required. Pick a form template and starting status, and you'll go straight into the workspace.",
      "Every step has a notes pane, and those notes are saved with the quote.",
    ],
    link: { label: "Create a cabinet quote", to: "/cabinet-quotes/new" },
    keywords: [
      "cabinet",
      "new quote",
      "create",
      "start",
      "kitchen",
      "template",
    ],
  },
  {
    id: "cabinet-customer-link",
    category: "Cabinet Quotes",
    question: "How do I send a cabinet quote to the customer?",
    answer: [
      "In the quote workspace, click Generate Customer Link. The link is copied to your clipboard automatically and also shown in a box with Copy and Open buttons.",
      "Only the quote's owner or a collaborator with full access can generate one. If the button is disabled, you have a more limited access level on that quote.",
      "The customer opens the link without signing in and sees a portal where they can review the quote and send you a message with images or PDFs attached.",
    ],
    keywords: [
      "customer link",
      "share",
      "send",
      "portal",
      "client",
      "email quote",
      "token",
    ],
  },
  {
    id: "cabinet-collaborators",
    category: "Cabinet Quotes",
    question: "What can cabinet quote collaborators do?",
    answer: [
      "Collaborators are added from the workspace at one of four levels: full access, checklist access, quote revisions access, or read only.",
      "Generating a customer link needs full access. Starting a revision needs either full access or quote revisions access — with anything less, the page tells you that you can't submit revisions for that quote.",
    ],
    keywords: [
      "collaborator",
      "permission",
      "share",
      "access",
      "read only",
      "team",
      "invite",
    ],
  },
  {
    id: "cabinet-render",
    category: "Cabinet Quotes",
    question: "How does the AI Render Enhancer work?",
    answer: [
      "Upload a render you've already produced in your design software and it comes back more photorealistic — the same scene, with better lighting, materials, and detail, upscaled toward 4K. It accepts images or PDFs.",
      "You can add optional style direction in plain English (for example “warmer daylight, richer wood grain”) and pick an output size, or leave it on Auto to match the source. Enhancing usually takes up to a minute.",
      "Once you have a result you can keep refining it with “Alter this render” — describe the change you want and apply it — or download the image.",
    ],
    link: {
      label: "Open the AI Render Enhancer",
      to: "/cabinet-quotes/design-render",
    },
    keywords: [
      "ai",
      "render",
      "image",
      "photorealistic",
      "enhance",
      "design",
      "upscale",
      "3d",
    ],
  },

  // ---------------------------------------------------------------- Fleet
  {
    id: "forklift-checklist",
    category: "Fleet",
    question: "How do I fill out a forklift checklist?",
    answer: [
      "Open the checklist from the link or code posted on the machine itself — the address carries the branch and unit number, so the right unit loads the right checklist. You can also reach it from the Check In button on your branch's forklift dashboard.",
      "Type your name first; the checklist won't appear until you do. Then work through the items one at a time. Each item has two answers — the good one (for example “No Leaks” or “Optimal Tires”) advances you automatically, and the second one reports a defect.",
      "You have to answer every item before it will submit. If you try early, it tells you how many you still have left.",
    ],
    steps: [
      "Open the checklist link for your branch and unit.",
      "Enter your name.",
      "Answer each item; a good answer moves you to the next one.",
      "For a defect, describe the problem and choose Save, or Not Safe if the unit shouldn't be used.",
      "Click Submit at the end.",
    ],
    note: "The checklist shows a photo upload box, but images are not currently sent with the submission — describe the problem in the defect text instead.",
    keywords: [
      "forklift",
      "checklist",
      "check in",
      "inspection",
      "daily check",
      "moffett",
      "lift",
      "equipment",
      "pre-trip",
    ],
  },
  {
    id: "forklift-not-safe",
    category: "Fleet",
    question: "What's the difference between Save and Not Safe on a defect?",
    answer: [
      "Save records the defect and lets you carry on through the rest of the checklist — use it for something worth reporting that doesn't stop the machine being used.",
      "Not Safe reports the unit immediately and takes you back to the branch dashboard without finishing the checklist. Use it when the forklift shouldn't be operated. It also takes the unit out of service, logged as a failed inspection with the item you reported, until someone puts it back in service.",
    ],
    keywords: [
      "not safe",
      "defect",
      "broken",
      "damage",
      "unsafe",
      "out of service",
      "lockout",
      "red tag",
    ],
  },
  {
    // id kept from when this was "Location Manager and Forklift Manager".
    id: "fleet-manager-pages",
    category: "Fleet",
    question: "Who can see the Fleet pages and manage forklifts?",
    answer: [
      "The Fleet menu section — Fleet Overview, Forklifts, Vehicles, Inspection Log, and Location Manager — is only for the Admin and Fleet Manager roles. Everyone else works with forklifts through the checklist on the machine and the branch kiosk, and with trucks through the mobile app.",
      "Each unit's check-in history is also on the branch kiosk: tap the Last Check-In date on a forklift's card. It shows the last 90 days from both the kiosk and the mobile app.",
    ],
    link: { label: "Open Fleet", to: "/fleet" },
    keywords: [
      "fleet manager",
      "location manager",
      "forklift manager",
      "vehicle manager",
      "add forklift",
      "units",
      "history",
      "records",
      "access",
    ],
  },
  {
    id: "fleet-find-inspection",
    category: "Fleet",
    question: "How do I find a past forklift or truck inspection?",
    answer: [
      "Open Inspection Log under Fleet. It lists every forklift check-in (from the mobile app and the branch kiosks) and every truck pre-trip inspection from the mobile app, newest first.",
      "Search by unit or truck number, inspector, flagged item, or notes, and narrow it with the branch, type (Forklifts or Vehicles), result, and date filters. Click any row to see the full checklist, the flagged items with their notes, and — for trucks — the odometer and how long the inspection took.",
      "Inspections the app timed show how long they took in a Time column, and the pages show the typical (median) time. Anything under 30 seconds is highlighted — that's too fast to have actually walked the whole checklist.",
      "Export CSV downloads exactly the rows you're looking at, with a column for every checklist answer. Each forklift's and truck's own page has an Export CSV button too.",
    ],
    steps: [
      "Open Fleet → Inspection Log.",
      "Pick a date range (Today, 7 days, 30 days, 90 days, or From/To dates).",
      "Search or filter, then click a row to open the inspection.",
    ],
    note: "History on the Fleet pages goes back 90 days. Export what you need to keep longer.",
    link: { label: "Open the Inspection Log", to: "/fleet/inspections" },
    keywords: [
      "inspection",
      "pre-trip",
      "pretrip",
      "checklist",
      "check-in",
      "history",
      "report",
      "export",
      "csv",
      "who checked",
      "flagged",
      "not safe",
      "truck",
      "forklift",
      "spreadsheet",
      "excel",
      "download",
      "how long",
      "time taken",
      "duration",
      "rushed",
    ],
  },
  {
    id: "fleet-truck-no-inspection",
    category: "Fleet",
    question: "Why does a truck show “Not in service” or “Drove, no inspection” when nobody filled anything in?",
    answer: [
      "The site reads every truck's GPS odometer twice a day — around 3:30 AM and 11:30 PM Eastern. If a truck had no pre-trip inspection that day, the two readings decide what the day was: if the odometer didn't move (less than half a mile), the truck is counted as Not in service; if it did move, the day shows as Drove, no inspection.",
      "These automatic days appear in the truck's history, its activity calendar, and the Inspection Log with “Automatic (GPS)” as the inspector. Click one to see both odometer readings. On a truck's page, the No inspection filter shows just these days.",
    ],
    note: "This started when the feature went live — earlier days have no readings. A truck someone marked out of service shows Out of service with its reason instead. Trucks whose GPS tracker has been silent for over a week aren't judged, since a dead tracker would look like a parked truck.",
    link: { label: "See trucks that drove with no inspection", to: "/fleet/inspections?result=missed" },
    keywords: [
      "not in service",
      "no inspection",
      "missed inspection",
      "drove without",
      "pre-trip",
      "odometer",
      "mileage",
      "parked",
      "automatic",
      "gps",
    ],
  },
  {
    id: "fleet-activity-calendar",
    category: "Fleet",
    question: "How do I read the Activity calendar on a forklift or truck?",
    answer: [
      "The Activity calendar on a forklift's or truck's page shows the last 90 days as month calendars, newest month first, with the date printed in each square. The same calendar is on the branch kiosk's check-in history.",
      "Each day is colored by the worst thing that happened that day. Green is Passed, yellow is Needs attention, and solid red is Not safe. A red outline means Drove, no inspection, purple means Out of service, and grey means Not in service. A plain square means Nothing recorded, and today has a dark outline. So a day with one passed and one Not safe inspection shows red.",
      "Under the calendar, the legend counts how many of the 90 days fell into each color. Hover over a day to see its date, what happened, and how many inspections or check-ins there were.",
      "Click a colored day to show only that day in the history below. Click it again, or the date chip above the history, to see every day again.",
    ],
    note: "Drove, no inspection and Not in service only show up for trucks, because they come from the truck's GPS odometer.",
    link: { label: "Open Fleet", to: "/fleet" },
    keywords: [
      "activity",
      "calendar",
      "colors",
      "colours",
      "green",
      "red",
      "yellow",
      "purple",
      "grey",
      "gray",
      "squares",
      "legend",
      "what does red mean",
      "today",
      "days",
      "forklift",
      "truck",
      "history",
    ],
  },
  {
    id: "fleet-out-of-service",
    category: "Fleet",
    question: "What's the difference between Out of service and Not in service?",
    answer: [
      "Out of service is something a person sets because a forklift or truck can't be used — it always has a reason (scheduled maintenance, repair / breakdown, waiting on parts, accident / damage, failed inspection, or other) and stays that way until someone puts it back in service.",
      "Not in service is automatic and only applies to trucks: no pre-trip inspection that day and the GPS odometer didn't move, so the truck simply wasn't used.",
      "Every change to out of service and back shows in the unit's history with who made it, the reason, and any details, and the Inspection Log's Out of service filter finds them across the fleet.",
    ],
    steps: [
      "Open the forklift or truck under Fleet.",
      "Click Mark out of service.",
      "Pick a reason, add details (required for Other) and an expected-back date if you know it.",
      "When it's fixed, click Mark in service and note what was done.",
    ],
    note: "Forklifts can also be taken out of service from the branch kiosk (it asks for a reason too), and a Not Safe report does it automatically. Changes made in the mobile app don't carry a reason.",
    link: { label: "See what's out of service", to: "/fleet/inspections?result=oos" },
    keywords: [
      "out of service",
      "down",
      "broken",
      "maintenance",
      "repair",
      "parts",
      "shop",
      "red tag",
      "not in service",
      "back in service",
      "reason",
    ],
  },
  {
    id: "fleet-shifts",
    category: "Fleet",
    question: "How do shifts work on the forklift kiosk?",
    answer: [
      "Branches that run more than one shift on the same forklifts — like SBC — have a button for each shift across the top of the branch kiosk. Pick yours before checking units in; the kiosk remembers it on that screen until someone picks another. Each forklift card shows which shifts have checked it in the last 12 hours.",
      "Every check-in records its shift, so the Fleet pages can filter a unit's history by shift. If you scan the code on a machine instead of using the kiosk, the checklist asks which shift you're on. Defect and Not Safe emails go to the shift's supervisor when one is set, otherwise to the branch's operations manager.",
      "Shifts and their supervisors are set per branch in Location Manager — open the branch, click Edit on its Shifts card, then Add shift and Save.",
    ],
    note: "SBC used to have a separate location per shift (SBC1, SBC2, SBC3). Their old kiosk links still work and open SBC with that shift picked, and their check-in history now shows under SBC.",
    link: { label: "Open Location Manager", to: "/fleet/forks/locationmanager" },
    keywords: [
      "shift",
      "1st shift",
      "2nd shift",
      "3rd shift",
      "sbc",
      "sbc1",
      "sbc2",
      "sbc3",
      "supervisor",
      "kiosk",
      "check in",
    ],
  },
  {
    id: "fleet-truck-location",
    category: "Fleet",
    question: "Where is a truck right now?",
    answer: [
      "Open Vehicles under Fleet and switch to Map to see every truck's latest GPS Insight position — blue is driving, amber is idling, green is parked, gray hasn't reported in a day. Click a pin for the truck, its driver if one is assigned, and a link to open it in Google Maps.",
      "The List view and each truck's own page show the same thing in words (Driving, Idling, Parked) with the landmark or address and how long ago it reported. The page refreshes itself every two minutes, or use Refresh.",
    ],
    note: "Trucks come from Bistrack and are matched to GPS trackers by VIN, then by truck number. A truck with no match shows No GPS; a tracker with no Bistrack truck is listed as GPS only.",
    link: { label: "Open Vehicles", to: "/fleet/vehicles" },
    keywords: [
      "gps",
      "map",
      "location",
      "where is",
      "truck",
      "vehicle",
      "driver",
      "gps insight",
      "tracking",
      "vin",
      "odometer",
      "mileage",
    ],
  },
  {
    id: "fleet-manage-truck",
    category: "Fleet",
    question: "How do I rename a truck, move it to another branch, or remove it?",
    answer: [
      "Open Vehicles under Fleet and click the truck. Edit lets you give it a different name and pick another branch from the list. To undo either one, click Use Bistrack's name or choose Use Bistrack's branch. A renamed truck still shows its Bistrack number (for example “Bistrack: #134”) under its name, and a moved one shows “Moved” under its branch.",
      "The trash can on the truck's page removes it: it's taken off every vehicle list, count, map and homepage widget, here and in the mobile app, so nobody is asked to inspect it. Its history is kept. To bring it back, click Removed on the Vehicles page, open the truck, and click Restore.",
      "These changes don't touch Bistrack. They're saved on Node and the mobile app uses them too, so drivers see the new name and branch. The truck keeps its GPS tracker and its inspection history.",
    ],
    note: "Two trucks can't have the same name. Past inspections keep the branch they were recorded under; the Inspection Log shows them under the truck's new name, and searching the old number still finds them. Trucks can't be added here, because they come from Bistrack. A change made in the mobile app can take up to 30 seconds to show here.",
    link: { label: "Open Vehicles", to: "/fleet/vehicles" },
    keywords: [
      "rename truck",
      "truck name",
      "move truck",
      "change branch",
      "wrong branch",
      "transfer",
      "remove truck",
      "delete truck",
      "sold",
      "retired",
      "hide",
      "restore",
      "removed",
      "bistrack",
      "vehicle",
    ],
  },
  {
    id: "fleet-manage-forklift",
    category: "Fleet",
    question: "How do I add, rename, move, or retire a forklift?",
    answer: [
      "Open Forklifts under Fleet and click Add forklift — enter the unit number, pick the branch, and check the box if it's a Moffett. New units start in service and show up on the branch kiosk and in the mobile app.",
      "To change an existing unit, click it in the list. Edit renames it, moves it to another branch, or changes the Moffett setting; its check-in history stays with it. Mark out of service asks for a reason and blocks check-ins on the branch kiosk until you choose Mark in service. The trash can deletes the unit from the fleet but keeps its history in the Inspection Log.",
      "You can also do all of this for one branch from its page in Location Manager: it lists that branch's forklifts with edit, out-of-service, and delete buttons on each row, and Add forklift starts with the branch already picked.",
    ],
    note: "A unit number can only be used once per branch. Branches come from Location Manager.",
    link: { label: "Open Forklifts", to: "/fleet/forklifts" },
    keywords: [
      "add forklift",
      "new forklift",
      "rename",
      "transfer",
      "move",
      "delete",
      "remove",
      "out of service",
      "in service",
      "moffett",
      "unit number",
    ],
  },
  {
    id: "fleet-locations",
    category: "Fleet",
    question: "How do I add, edit, or remove a branch location?",
    answer: [
      "Open Location Manager under Fleet. Each location is one row showing its forklift count and managers. Search by location, manager, email, or phone, and click a row to open that location's page.",
      "A location's page has its contacts, shifts, and alert thresholds — click Edit on a card, make your changes, and Save. Its forklifts are listed below: click one to see its check-ins, or use the buttons on its row to edit the unit, mark it out of or back in service, or delete it. Add forklift adds one already set to that branch. Open kiosk opens the branch kiosk in a new tab.",
      "To remove a location, click the trash can at the top of its page. If it still has forklifts, pick the location they should move to first. Its past check-ins stay in the Inspection Log.",
    ],
    steps: [
      "Open Fleet → Location Manager.",
      "Click Add location and type the branch's name, or click an existing location.",
      "Click Edit on Contacts, Shifts, or Alert thresholds, then Save.",
    ],
    note: "A location's name can't be changed after it's added — forklifts, kiosk links, and check-ins all refer to it by name.",
    link: { label: "Open Location Manager", to: "/fleet/forks/locationmanager" },
    keywords: [
      "location manager",
      "branch",
      "add location",
      "new branch",
      "remove location",
      "delete location",
      "close branch",
      "contacts",
      "operations manager",
      "general manager",
      "phone",
      "email",
      "alert",
      "kiosk",
      "forklifts",
    ],
  },

  // ---------------------------------------------------------------- Tools
  {
    id: "weather",
    category: "Dashboard & Tools",
    question: "Where is the branch weather page?",
    answer: [
      "Weather is under Everyday Tools in the menu. There are also full-screen weather displays used on branch monitors, which are opened with a branch-specific link rather than through the menu.",
    ],
    link: { label: "Open weather", to: "/dashboard/weather" },
    keywords: ["weather", "forecast", "rain", "temperature", "display", "tv"],
  },
  {
    id: "sharepoint",
    category: "Dashboard & Tools",
    question: "How do I get to SharePoint documents from Node?",
    answer: [
      "There's a SharePoint link under Everyday Tools in the menu. SBCF truss and panel documents have their own page under the SBCF section.",
    ],
    link: { label: "Open SharePoint", to: "/dashboard/sharepoint" },
    keywords: ["sharepoint", "documents", "files", "drive", "folder"],
  },

  {
    id: "pcw-hours-dashboard",
    category: "Dashboard & Tools",
    question: "How do I see PCW punch in/out hours by work order?",
    answer: [
      "The PCW Hours dashboard shows time workers punch in and out of work orders on the shop-floor mobile app. Click Admin in the menu to open the Admin Center — it's under Shop Floor.",
      "The Hours tab totals hours by work order, by worker, and by category for the selected date range — use the Today / Last 7 Days / Last 30 Days / All Time buttons, or pick your own from and to dates. Anyone currently punched in shows in a green Punched In Right Now banner at the top.",
      "Custom jobs — ones workers create on the shop tablet when the work isn't in the work order list — show in Hours by Work Order by name with an amber Custom job tag, right alongside the numbered work orders. The search box finds them by name.",
      "Click a row in Hours by Work Order to open that job's full breakdown: total hours, every punch on the job, and per-worker and per-category totals for just that job.",
      "The Open Jobs tab lists every open work order and unfinished custom job with its total time to date (all time, not the date range), who's working on it now, and its allotted time and progress if one is set.",
      "The Punch Log tab lists individual punches, newest first, with filters for worker, category, and still-punched-in vs closed, plus a WO # or job name box — type a work order number, or part of a custom job, job, or customer name.",
      "Punches can be corrected from the dashboard. Fix on any punch changes its worker, category, or times; Force Punch Out closes an open punch at the current time; and Add Manual Punch (on the Punch Log tab or inside a job) records time someone worked without punching — on the Punch Log tab, pick Work Order # and type the number, or pick Custom Job and choose one. Category is optional: the shop tablet no longer asks for one, so its punches show a dash in the Category column. Corrected and manual punches are marked in the log.",
      "Every table has a CSV button in its header that downloads what's shown for Excel.",
    ],
    link: { label: "Open PCW Hours", to: "/pcw" },
    note: "PCW Hours is limited to the Admin and PCW Admin roles. Punching in and out happens on the mobile app, not on the website.",
    keywords: [
      "pcw",
      "punch",
      "punch in",
      "punch out",
      "time clock",
      "timeclock",
      "hours",
      "work order hours",
      "shop hours",
      "forgot to punch out",
      "labor",
      "fix punch",
      "edit punch",
      "manual punch",
      "missed punch",
      "custom job",
      "custom work order",
      "job name",
      "csv",
      "export",
      "excel",
    ],
  },
  {
    id: "pcw-manage-workers",
    category: "Dashboard & Tools",
    question: "How do I add or manage PCW workers and categories?",
    answer: [
      "Workers and categories are managed on the PCW Hours dashboard, on the Workers and Categories tabs.",
      "Workers punch in with a passcode, so each active worker needs their own — the dashboard won't let two active workers share one. There's no delete: use Deactivate so their punch history keeps its name, and Activate to bring someone back. If a worker's face recognition is acting up, the small reset button next to Enrolled clears their Face ID so they can re-enroll on the app.",
      "The shop tablet no longer asks workers for a category — its punches come in as None. Categories can still be set on a punch from the dashboard (Fix, or Add Manual Punch), and older punches keep theirs. They work like workers — Rename and Deactivate/Activate — and the up/down arrows set their order in the punch dialog's Category list. Renaming a category only affects future punches; past punches keep the name they were punched under.",
    ],
    steps: [
      "Click Admin in the menu, then PCW Hours under Shop Floor.",
      "Switch to the Workers or Categories tab.",
      "Click Add Worker (name + passcode) or Add Category (name).",
      "Use Edit, Rename, Deactivate, or the order arrows on any row.",
    ],
    link: { label: "Open PCW Hours", to: "/pcw?view=workers" },
    note: "PCW Hours is limited to the Admin and PCW Admin roles.",
    keywords: [
      "pcw",
      "add worker",
      "new worker",
      "passcode",
      "pin",
      "deactivate",
      "categories",
      "face id",
      "face recognition",
      "reorder",
    ],
  },
  {
    id: "pcw-custom-jobs",
    category: "Dashboard & Tools",
    question: "How do I rename or finish a PCW custom job?",
    answer: [
      "When the work isn't in the shop tablet's work order list, workers can create a custom job on the tablet and punch into it. Custom jobs show on the PCW Hours dashboard by name with an amber Custom job tag, alongside the numbered work orders.",
      "The Custom Jobs tab lists every custom job with its total hours (all time), number of punches, who created it and when, and whether it's Active or Finished — plus how many people are punched into it right now. Click a job's name to open all of its punches.",
      "Rename fixes a typo or a vague name; the new name also replaces the old one on the job's past punches. Mark Finished when the work is done — the job comes off the Job Hours screen on the shop floor TV (unless someone is still punched into it). Reopen puts it back. The same Rename and Mark Finished / Reopen buttons are at the top of the job's own page.",
    ],
    steps: [
      "Click Admin in the menu, then PCW Hours under Shop Floor.",
      "Switch to the Custom Jobs tab.",
      "Click Rename, or Mark Finished / Reopen, on the job's row.",
    ],
    link: { label: "Open Custom Jobs", to: "/pcw?view=jobs" },
    note: "PCW Hours is limited to the Admin and PCW Admin roles. Custom jobs are created on the shop tablet, not on the website.",
    keywords: [
      "pcw",
      "custom job",
      "custom work order",
      "other job",
      "no work order",
      "rename job",
      "finish job",
      "close job",
      "done",
      "job hours",
      "shop tablet",
    ],
  },
  {
    id: "pcw-allotted-time",
    category: "Dashboard & Tools",
    question: "How do I set an allotted time (budget) for a PCW job?",
    answer: [
      "Each work order or custom job can have an allotted time — how long it should take. The shop floor TV's Job Hours screen then shows the job's total time against it with a progress bar, which turns amber at 90% and red once the job goes over.",
      "On the PCW Hours dashboard, the Open Jobs tab lists every open work order (the same list the shop tablet shows) and unfinished custom job, including ones nobody has started yet, so you can set the time before work begins. Click Set Time on a row, enter hours and minutes, and click Save. Rows with an allotted time show it with a progress bar and how much is left or over, and the tiles at the top count the jobs with an allotted time and the ones over it.",
      "You can also set it from a job's own page (click any job row): the Allotted time box at the top shows the time used so far against it, and its Set Allotted Time / Change button opens the same box. To remove an allotted time, open Change and click Clear.",
      "Progress counts all time on the job, including anyone punched in right now — not just the date range picked on the Hours tab.",
    ],
    steps: [
      "Click Admin in the menu, then PCW Hours under Shop Floor.",
      "Switch to the Open Jobs tab.",
      "Click Set Time (or Change) on the job's row.",
      "Enter the hours and minutes and click Save.",
    ],
    link: { label: "Go to Open Jobs", to: "/pcw?view=open" },
    note: "PCW Hours is limited to the Admin and PCW Admin roles. The TV only lists jobs that have time on them, so a budgeted job shows up there once someone punches into it.",
    keywords: [
      "pcw",
      "allotted time",
      "allotted hours",
      "budget",
      "estimate",
      "estimated hours",
      "job budget",
      "over budget",
      "progress",
      "how long",
      "hours left",
      "open jobs",
      "shop floor tv",
    ],
  },
  {
    id: "send-app-codes",
    category: "Dashboard & Tools",
    question: "How do I text or email someone a Node Mobile download code?",
    answer: [
      "Node Mobile installs with one-time download codes bought in Apple Business Manager. The Mobile App Codes page (click Admin in the menu, then look under Node Apps) sends them out — by text message, or by email from noreply@bigclumber.com.",
      "Type a phone number or an email address in Send a code and click Send code — a phone number gets a text, an email address gets an email. The next unused code goes out automatically, and the page shows which code it was and how many are left.",
      "Someone who already got a code gets the same code again, since they only need one. If their code was used by someone else, check Send a new code even if they already got one and send again.",
      "The Activity tab lists every send — from the page or the script API — with the code, who sent it, and any error. Search it by phone number, email, code, or person to see who has which code.",
      "To see whether someone actually tapped their link, look for Opened or Not opened next to the send in Activity — Opened also shows when they first tapped it. Use the All / Opened / Not opened buttons to list just the people who haven't opened theirs yet, so you know who to follow up with. The Opened tile at the top counts the codes whose link has been opened.",
      "The download link in every text and email is a node.bigclumber.com link that forwards to the App Store, which is how the page knows it was opened. Link previews and mail scanners that identify themselves aren't counted, but some email security scanners can still show up as an open. Sends from before links were tracked don't show Opened or Not opened.",
      "The Message tab changes what people get: the text message and the email's subject and message. {link} becomes their download link and {code} the code itself; the email always adds a Get Node Mobile button and the code under your message.",
      "Codes sent by email get one automatic reminder email if, 2 days later, nobody has opened the link and the person hasn't signed in to Node Mobile. Reminders go out on weekdays between 8 AM and 5 PM Eastern, show in Activity as Reminder, and count opens like any other send. A re-send restarts the 2 days, and nobody gets more than one reminder per code. Texted codes don't get reminders. To change the reminder or turn it off, use the Reminder email section of the Message tab, then click Save message.",
      "When you buy more codes, open the Import codes tab, click Choose spreadsheet…, and pick the code spreadsheet Apple Business Manager gives you (.xls, .xlsx, or .csv). Codes that are already in the pool are skipped, so importing the same file twice is safe. The page warns you when fewer than 25 codes are left.",
      "The Script API tab shows the URL for sending a code from a script without signing in: /api/appcodes/send?to=<phone or email>&key=<key>.",
    ],
    steps: [
      "Click Admin in the menu, then Mobile App Codes under Node Apps.",
      "Type the person's phone number or email address.",
      "Click Send code.",
    ],
    link: { label: "Open Mobile App Codes", to: "/admin/app-codes" },
    note: "Mobile App Codes is limited to the Admin and IT roles. The script API only works once APP_CODES_API_KEY is set on the server.",
    keywords: [
      "node mobile",
      "mobile app",
      "app code",
      "download code",
      "redemption code",
      "redeem",
      "vpp",
      "apple business manager",
      "clicksend",
      "sms",
      "text",
      "install app",
      "iphone",
      "api",
      "script",
      "import codes",
      "opened",
      "clicked",
      "tapped",
      "tracking",
      "did they click",
      "follow up",
      "reminder",
      "remind",
      "not redeemed",
      "didn't install",
    ],
  },
  {
    id: "assistant-usage",
    category: "Dashboard & Tools",
    question: "How do I see how people use the Node Mobile assistant and Siri?",
    answer: [
      "Assistant Usage (click Admin in the menu, then look under Node Apps) shows every request made to the assistant in Node Mobile (Home → Ask Assistant) and through Siri (\"Hey Siri, ask Node\"). Pick 7 days, 30 days, 90 days, or All time at the top.",
      "The tiles count requests and the people who made them, how many came through Siri, things it couldn't do, Order Problems and Safety Concerns submitted through it, problems (errors or failed commands), and an estimated OpenAI cost. Click Requests, Through Siri, Couldn't do, or Problems to jump to that list.",
      "The Couldn't do tab is the list of things people asked for that the assistant has no command for yet, grouped by what they wanted with the most-asked first, plus who asked and their exact words. Use it to decide what to add next. Below it, See them lists requests it answered without running any command, which can also be things it couldn't do.",
      "The Requests tab lists each request, newest first: who asked, whether it was the app or Siri, what they said, what the assistant answered, and the commands it ran. Search by person or words, or narrow it to Siri, Couldn't do, No command, or Problems. The Commands tab ranks the commands by use, and the People tab shows who uses it most.",
    ],
    steps: [
      "Click Admin in the menu, then Assistant Usage under Node Apps.",
      "Pick a time period.",
      "Open the Couldn't do tab to see what people want added.",
    ],
    link: { label: "Open Assistant Usage", to: "/admin/assistant-usage" },
    note: "Assistant Usage is limited to the Admin role. Order Problem requests are anonymous, so they're counted but show no name or words. Siri requests that never reach Node, for example when Siri didn't hear \"ask Node\", aren't counted. Couldn't do and Submitted only cover requests from late October 2026 on.",
    keywords: [
      "siri",
      "voice assistant",
      "ask node",
      "ask assistant",
      "node mobile",
      "usage",
      "stats",
      "analytics",
      "feature requests",
      "what people ask",
      "couldn't do",
      "cost",
      "openai",
      "logs",
    ],
  },
  {
    id: "signage-change-content",
    category: "Dashboard & Tools",
    question: "How do I change what plays on the signage TVs?",
    answer: [
      "The TVs around the branches are Raspberry Pi displays that loop a playlist of images and videos. They're managed on the Digital Signage page — click Admin in the menu, then Digital Signage under Communications. The page has a Displays tab (every TV), Playlists and Media tabs (the content), and — for admins and the Executive Team — a Branches tab.",
      "Content lives in a library: your branch's, or the Company-wide one. On the Media tab, pick the library at the top, then click Upload or just drag images or videos anywhere onto the tab (jpg, png, gif, bmp, webp, mp4, mkv, mov, avi — screens are 1920×1080 landscape), or click From YouTube to bring in a clip of a YouTube video. Each file shows which playlist uses it, or Unused. Search by name, filter to Images, Videos, or Unused, and sort by newest, name, or size. Click a thumbnail to see the image full size or play the video (with a Download button). Hover a file for its pencil (edit an image: resize it, add text and a background) and trash icons. To clear out several at once, tick the circle in each thumbnail's corner and click Delete in the bar that appears.",
      "On the Playlists tab (same library picker), type a name and click + to create a playlist, or pick one from the list. Click Add media above the slide list to add images and videos — search or filter to Images or Videos, click the files you want (they're numbered in the order you pick them, which is the order they're added), and click Add to playlist. Each slide is one row: an image has a seconds-on-screen box, and a video has a speaker button to mute or unmute it (Mute all appears when there are several videos). Click the arrow at the end of a row for more: Start at / Stop at to trim a video, Show from / Show until to schedule just that slide, and Move up / Move down. Drag a row by its grip on the left to reorder. Shuffle (under Playback on the right) is on for every playlist unless you turn it off: the slides play in a new random order each time through, and with Shuffle off they play in the order shown. Click Save when done — the header shows Unsaved changes until you do. Playing on (right side) lists every display the playlist is on — click one to jump to it.",
      "To put a playlist on a TV, go to the Displays tab and click the display. In the panel that opens, use + Add playlist under What it plays. A display can play more than one playlist — for example your branch's Default plus a company-wide Seasonal one. They play together as one loop, top to bottom; use the arrows to reorder and the × to take one off. Changes save right away and show up on the TV within moments — no reboot needed.",
      "A playlist can have its own date range: under Schedule on the right, set From and/or Until and click Save. Outside those dates the whole playlist is skipped on every display it's on, while the display's other playlists keep playing. Displays download it ahead of time, so it starts right on the first day. Dated playlists are marked scheduled, running, or ended.",
      "Playlists can also include live slides, each with its own on-screen duration. They're under Live slides on the right. The weather slide refreshes itself every 15 minutes; pick Once per loop or Every few slides (and how many slides go between weather slides), plus its seconds on screen. It needs two one-time setups by an admin: the branch's weather location (the pencil on the branch's card on the Branches tab) and the OpenWeather API key (the Settings button at the top right of the page — Test key there checks the key works and shows the current weather at one of your branches; the Weather card also lists any branch still missing a location). Priebes Creative Woodworking's playlists also get an On the Clock slide (it's only offered there, because it shows Priebes' own PCW time clock) showing who's punched in right now — worker, job (work order or custom job), and time on the job. For a screen dedicated to the punch board, switch the display to Live PCW board in its panel — the TV then shows a truly live page with a ticking clock and second-by-second job timers rather than rotating slides, and it rotates between On the Clock and a Job Hours screen (total time on each open job) for as many seconds each as you set.",
    ],
    steps: [
      "Click Admin in the menu, then Digital Signage under Communications.",
      "On the Media tab, pick the library and upload images/videos.",
      "On the Playlists tab, build or edit a playlist (optionally set a Schedule) and click Save.",
      "On the Displays tab, click a display and use + Add playlist — add more than one to play them together.",
    ],
    link: { label: "Open Digital Signage", to: "/signage" },
    note: "Digital Signage is limited to Admin and Executive Team (all branches) and General Managers (their own branch). The Branches tab, the Settings button, and the Company-wide library are Admin/Executive Team only.",
    keywords: [
      "tv",
      "tvs",
      "screen",
      "screens",
      "display",
      "signage",
      "slideshow",
      "slide",
      "advertising",
      "promo",
      "playlist",
      "multiple playlists",
      "two playlists",
      "shuffle",
      "random order",
      "schedule",
      "date range",
      "seasonal",
      "holiday",
      "media loop",
      "raspberry pi",
      "video wall",
      "change picture on tv",
      "mute",
      "sound",
      "audio",
      "volume",
      "preview",
      "upload",
      "weather",
      "on the clock",
      "punched in",
      "shop floor board",
    ],
  },
  {
    id: "signage-pcw-board",
    category: "Dashboard & Tools",
    question: "How do I change what the shop floor PCW board shows, or how long each screen stays up?",
    answer: [
      "A display at Priebes Creative Woodworking can run the Live PCW board full-time instead of playlists: on the Digital Signage Displays tab, click the display and pick Live PCW board under What it plays.",
      "The board rotates between two screens. On the Clock lists who's punched in, the job they're on (a work order, or a custom job created on the shop tablet), and how long they've been on it. Job Hours lists the open jobs that have time on them — the work orders the shop tablet offers plus custom jobs that haven't been marked finished — with each job's total time (all time) and who's on it right now or when it was last worked. Jobs being worked right now are listed first, and their totals keep ticking.",
      "When a job has an allotted time (set on the PCW Hours dashboard), its total shows against it — for example 12h 30m / 40h — with a progress bar and percent. The bar turns amber at 90% and red once the job is over, and a red tag at the top counts the jobs over their allotted time.",
      "Under What it plays, each screen has an on/off switch and a seconds box. Change them and click Save — the TV picks it up within a few seconds. Turn a screen off to show only the other one; at least one has to stay on. When a screen has more rows than fit on the TV, it pages through them, each page for that many seconds. The tags at the bottom right of the TV show which screen (and page) is up.",
    ],
    steps: [
      "Click Admin in the menu, then Digital Signage under Communications.",
      "On the Displays tab, click the shop floor display.",
      "Under What it plays, pick Live PCW board.",
      "Turn On the Clock and Job Hours on or off, set each one's seconds, and click Save.",
    ],
    link: { label: "Open Digital Signage", to: "/signage" },
    note: "The Live PCW board is only offered at Priebes Creative Woodworking. A playlist can include the On the Clock slide, but Job Hours is only on the Live PCW board.",
    keywords: [
      "pcw",
      "pcw board",
      "live board",
      "shop floor",
      "shop floor tv",
      "job hours",
      "hours per job",
      "time per work order",
      "work order hours",
      "on the clock",
      "how long",
      "seconds",
      "rotate",
      "screen time",
      "custom job",
      "allotted time",
      "budget",
      "progress",
      "over budget",
    ],
  },
  {
    id: "signage-tv-problems",
    category: "Dashboard & Tools",
    question: "How do I check on a signage TV or fix one that isn't working?",
    answer: [
      "Open Digital Signage and stay on the Displays tab. The bar at the top counts your displays, with a colored strip showing how many are healthy (green), online with a warning (amber), and offline (red). The All / Needs attention / Offline / Online buttons below it show a count each and filter the list. You can also search by display name, PIN, or branch, and admins can narrow the list to one branch. Click a branch's name to collapse or expand its displays.",
      "Each row shows a green dot when the display is checking in, the playlists it plays, what's on screen right now (a file name, Weather, or On the Clock — on player software v1.7.2 and newer), and its status: OK, Offline, Never connected, or chips that flag problems: Nothing assigned (no playlist), Nothing scheduled today (every playlist on it is outside its dates), Power warning (the Pi reported under-voltage — usually the power supply), a failed remote action, or Update pending (the player software updates itself within the hour).",
      "Click a display to open its panel. It explains each problem, shows its health — temperature, free storage, uptime, network, and player software version — and has remote actions: Identify (flashes the PIN on the TV so you can tell displays apart), Refresh content, Restart app, Reboot, and Update software. Actions reach the display at its next check-in, within about 30 seconds.",
      "A display shows as offline when it hasn't checked in for a couple of minutes. Remote actions can't reach an offline TV — check that the TV and the Pi behind it have power and a network connection.",
    ],
    steps: [
      "Open Digital Signage — you land on the Displays tab.",
      "Click Needs attention or Offline to see the TVs with problems.",
      "Click a display to see what's wrong, its health, and remote actions.",
      "Try Refresh content or Restart app first; Reboot if that doesn't help.",
    ],
    link: { label: "Open Digital Signage", to: "/signage" },
    note: "If a display stays offline after you've checked its power and network, contact IT.",
    keywords: [
      "signage",
      "tv",
      "tv offline",
      "tv not working",
      "blank screen",
      "black screen",
      "frozen",
      "stuck",
      "reboot tv",
      "restart tv",
      "identify",
      "health",
      "power warning",
      "update",
    ],
  },
  {
    id: "signage-edit-image",
    category: "Dashboard & Tools",
    question: "How do I resize an image or add text or a background for the signage TVs?",
    answer: [
      "On the Digital Signage page's Media tab, hover an image and click its pencil icon (or click the image and choose Edit image). The editor opens with the picture on a TV-shaped screen — 1920×1080 — and a blurred copy of the photo filling any space it doesn't cover. Images marked Large are bigger than the TVs need; saving from the editor shrinks them to screen size.",
      "The editor has a tool rail on the left, the picture in the middle, and settings for whatever is selected on the right. With nothing selected (or Canvas in the tool rail), the right side shows Screen and Background. Screen picks the shape: TV landscape, TV portrait, or the photo's own shape. Click the photo to Fit to screen (whole picture shows) or Fill screen (covers the screen, edges may be cut off), rotate or mirror it, and adjust brightness, contrast, and saturation. Crop (or double-click the picture) lets you drag the edges or corners to cut away what you don't want — pick Free, Original, Screen, 16:9, 4:3, 1:1, or 9:16 to lock the shape — then click Apply crop (Enter) or Cancel (Esc). With nothing selected, the Background panel switches the fill between a blurred photo, a solid color, a gradient, or another library image, and can darken it.",
      "The tool rail adds Text, a Banner (a colored box — handy behind text), a Circle, a Logo, or a QR code on top. Logo lets you pick an image already in the library or upload a new one (it's added to the library too); PNG logos with a transparent background look best. QR code asks for a website address (bigclumber.com/sale works — https:// is added for you), lets you pick the code color, a background color or transparent, and the border, and shows a live preview; click Add to image, then drag it into place. To change where it points later, select it and click Edit QR code…. Test it with a phone before it goes on the TVs. Drag anything to move it — it snaps to the center — and drag its corners to resize. Text has a font, size, color, bold/italic, alignment, a drop shadow, an outline, and an optional background band; drag the right-edge handle to change where lines wrap. A small toolbar floats above whatever is selected: duplicate, bring forward, send back, crop (pictures), edit (QR codes), and delete. The Layers list selects things that are hard to click, and its eye icon hides a layer (hidden layers aren't saved in the image). Zoom with the − / + buttons under the picture or Ctrl/⌘ + scroll, and click Fit to see the whole screen again. Undo/Redo are at the top (Ctrl/⌘+Z), next to an Unsaved changes marker.",
      "Click Save…. Replace the original updates the image everywhere it's used — every playlist and display picks up the new version on its next check-in. Save as a new copy keeps the original and adds the copy to the library. Either way your text and layers are kept, so you can reopen it and fix a typo later.",
    ],
    steps: [
      "Open Digital Signage → Media tab, hover the image, and click the pencil icon.",
      "Pick the screen shape, then fit or fill the photo and choose a background.",
      "Add text, banners, a logo, or a QR code from the tool rail and drag them into place.",
      "Click Save… and choose Replace the original or Save as a new copy.",
    ],
    link: { label: "Open Digital Signage", to: "/signage" },
    note: "Images are saved as JPEGs at screen size. Animated GIFs are saved as a still image. Videos can't be edited here — trim them in the playlist instead.",
    keywords: [
      "edit image",
      "photo editor",
      "resize",
      "crop",
      "cut out",
      "trim picture",
      "shrink",
      "optimize",
      "too big",
      "add text",
      "caption",
      "background",
      "blur",
      "logo",
      "upload logo",
      "qr code",
      "qr",
      "scan",
      "link to website",
      "banner",
      "signage",
      "tv",
    ],
  },
  {
    id: "signage-youtube-clip",
    category: "Dashboard & Tools",
    question: "How do I put a YouTube video (or part of one) on the signage TVs?",
    answer: [
      "On the Digital Signage page, open the Media tab, pick the library at the top, and click From YouTube. Paste the video's link (a youtube.com/watch, youtu.be, or Shorts link) — it loads as soon as you paste it (or click Load video).",
      "The video appears in a preview player with a timeline under it; a red line shows where the video is playing. Drag the two handles to choose the part to keep — a clip can be at most 3 minutes, so if you stretch past that the other handle follows along. For exact times, type into the Start and End boxes (like 1:23), use the − / + buttons to nudge by a second, or play the video and click Now to use the current spot. The Length chips (15s, 30s, 1:00, 3:00 max) set the end from the start. Preview clip plays just your selection (click Stop to stop it).",
      "Change the name under Save as if you like, then click Import (the button shows the clip length, like Import 0:30 clip). The server downloads just that section and saves it to the library as an MP4 — it shows on the Media tab with a progress bar and takes a minute or two. When it says Added, the clip is in the library with a YouTube icon; add it to a playlist like any other video.",
    ],
    steps: [
      "Open Digital Signage → Media tab and pick the library.",
      "Click From YouTube and paste the link — the video loads right away.",
      "Drag the handles (or type Start and End times) to pick up to 3 minutes, then click Import.",
      "When the import says Added, add the clip to a playlist on the Playlists tab.",
    ],
    link: { label: "Open Digital Signage", to: "/signage" },
    note: "Only import videos Big C owns or has permission to use. Live streams, private videos, and age-restricted videos can't be imported. Some videos don't allow previews outside YouTube — you can still pick the times and import them.",
    keywords: [
      "youtube",
      "youtube link",
      "video link",
      "clip",
      "trim video",
      "cut video",
      "part of a video",
      "download video",
      "signage",
      "tv",
    ],
  },
  {
    id: "signage-company-playlists",
    category: "Dashboard & Tools",
    question: "How do I put the same playlist on signage TVs at every branch?",
    answer: [
      "Admins and the Executive Team can build company-wide playlists that any branch's displays can play. On the Digital Signage page, open the Media or Playlists tab and choose Company-wide (all branches) in the Library picker at the top.",
      "Upload the images and videos on the Media tab (the company library is separate from each branch's), then build the playlist on the Playlists tab the same way as a branch one. Set a Schedule (From / Until) for seasonal or promo content so it turns itself on and off.",
      "Then go to the Displays tab, click a display at any branch, and use + Add playlist — company playlists show a globe icon and \"Company-wide\" in the list. Add it next to the display's own playlist and the two play together as one loop. The playlist's Playing on box lists every display it's on, with their branches.",
      "The weather slide works on company playlists too and shows each display's own branch. The On the Clock slide isn't offered on company playlists — it's only available on Priebes Creative Woodworking's own playlists.",
    ],
    steps: [
      "Open Digital Signage, go to the Media tab, and pick Company-wide in the Library picker.",
      "Upload the media.",
      "On the Playlists tab (still Company-wide), create the playlist, add media, optionally set a Schedule, and click Save.",
      "On the Displays tab, click each display and add the playlist with + Add playlist.",
    ],
    link: { label: "Open Digital Signage", to: "/signage" },
    note: "Only Admin and Executive Team can create, edit, or delete company-wide playlists and media. General Managers see company playlists in + Add playlist and can add them to, or remove them from, their own branch's displays. Deleting a company playlist takes it off every display at every branch.",
    keywords: [
      "signage",
      "tv",
      "company wide",
      "all branches",
      "every branch",
      "every location",
      "global playlist",
      "shared playlist",
      "seasonal",
      "holiday",
      "promo",
      "schedule",
      "date range",
      "multiple playlists",
    ],
  },
  {
    id: "signage-branches",
    category: "Dashboard & Tools",
    question: "How do I add or set up a signage branch (location, GM access, weather)?",
    answer: [
      "Admins and the Executive Team manage signage branches on the Digital Signage page's Branches tab. Each branch is one location with its own displays, media, and playlists. The bar at the top counts branches and displays and says how many branches still need a weather location.",
      "Each branch card shows its displays online (with a green/amber/red strip), buttons that jump to its displays or playlists, and a setup checklist: who manages it (its General Manager, or admins only), whether its weather location is set, and the PCW board for Priebes Creative Woodworking. Click any checklist line, or the pencil, to fix it.",
      "Click New branch (or the pencil on a card) to open the branch settings. Under Location & access, pick the Big C branch — its General Manager gets access automatically — or choose Custom location… to name it yourself and pick which GM (if any) can manage it. Under Weather location, paste the coordinates: in Google Maps, right-click the building, click the numbers to copy them, and paste into either box — both fill in. Check on map opens the spot to confirm it.",
      "Delete branch is at the bottom of the branch settings. It permanently removes the branch with all of its displays, playlists, and media.",
    ],
    steps: [
      "Open Digital Signage → Branches tab.",
      "Click New branch, or the pencil on an existing branch.",
      "Pick the branch (or a custom location and its GM access).",
      "Paste the weather coordinates from Google Maps and click Save.",
    ],
    link: { label: "Open Digital Signage", to: "/signage" },
    note: "The Branches tab and the Settings button (OpenWeather key, automatic player updates, YouTube import status) are Admin/Executive Team only. The PCW On the Clock option only appears for Priebes Creative Woodworking.",
    keywords: [
      "signage",
      "branch",
      "location",
      "new branch",
      "add branch",
      "delete branch",
      "general manager",
      "gm access",
      "weather",
      "coordinates",
      "latitude",
      "longitude",
      "google maps",
    ],
  },
  {
    id: "signage-register-device",
    category: "Dashboard & Tools",
    question: "How do I set up a new signage TV (register a Raspberry Pi)?",
    answer: [
      "A brand-new signage Pi walks you through its own setup on the TV, with a Connect → Install → Ready tracker at the top. First it gets online: it uses an Ethernet cable or the Big C Guest / PCW_Guest Wi-Fi automatically. If none of those work within about a minute, the screen shows phone steps instead — on your phone, join the Wi-Fi network shown on the TV (BigC-Signage-, then its PIN; no password), a setup page opens, and you pick the location's Wi-Fi and type its password. If the password is wrong, the TV says so and you can try again.",
      "Once it's online the TV shows Installing the display software with a progress bar and the current step. This takes about 10–20 minutes — leave it plugged in. It then says All set!, restarts by itself, and shows its PIN (like 1234-5678) full-screen until the display is registered.",
      "On the Digital Signage page's Displays tab, click Register display (or Add display next to the branch's name). Pick the branch the TV is at, enter the PIN from the TV screen, and give the display a name people will recognize (for example \"Front counter TV\").",
      "The display's panel opens right away so you can add its playlists. Within a few seconds the TV stops showing the PIN and starts playing them. The green dot shows the display is checking in — a display that's been unplugged goes offline within a couple of minutes.",
      "Remove display (at the bottom of its panel) un-registers the PIN — within about a minute the TV clears its content and goes back to showing the PIN screen until it's registered again (a TV that's offline switches once it's back online; displays on player software older than 1.7.4 can keep playing for a couple of passes through their playlist first).",
    ],
    steps: [
      "Plug in the Pi and follow the setup screens on the TV (connect it to the internet if it asks).",
      "When setup finishes, note the PIN it shows on the TV.",
      "Open Digital Signage and click Register display on the Displays tab.",
      "Pick the branch, enter the PIN and a name, and click Register.",
      "In the panel that opens, add a playlist with + Add playlist.",
    ],
    link: { label: "Open Digital Signage", to: "/signage" },
    note: "The Pi needs its setup files before this step. Admins can download them from Settings (top right of the Digital Signage page) → Set up a new display → Download setup files: flash Raspberry Pi OS Lite (64-bit), copy everything inside the zip's boot-files folder onto the stick's boot drive, and plug it in — the TV then guides you through getting it online. If it's been on the Installing screen for over 30 minutes, or shows Setup hit a snag more than once, contact IT.",
    keywords: [
      "signage",
      "new tv",
      "register",
      "pin",
      "raspberry pi",
      "install tv",
      "add display",
      "offline",
      "online",
      "tv stuck on pin",
      "setup screen",
      "stuck installing",
      "no internet",
      "wifi setup",
      "hotspot",
    ],
  },

  // ---------------------------------------------------------------- Messages
  {
    id: "send-direct-message",
    category: "Messages",
    question: "How do I send a direct message to a coworker?",
    answer: [
      "Click the chat icon in the top-right corner of any page (next to the notification bell) — it opens Messages and shows a badge when you have unread messages. Messages is also in the menu under Everyday Tools.",
      "Click the New button at the top of the conversation list, search for the person by name, branch, or email, then click their name to open the conversation.",
      "Type in the box at the bottom and press Enter to send — Shift+Enter adds a new line. Your conversations here are the same ones as chat in the Big C mobile app, so a message sent from either place shows up in both.",
    ],
    steps: [
      "Click the chat icon in the top-right corner (or open Messages under Everyday Tools in the menu).",
      "Click New at the top of the conversation list.",
      "Search for the person and click their name.",
      "Type your message and press Enter to send.",
    ],
    link: { label: "Open Messages", to: "/messages" },
    keywords: [
      "chat",
      "dm",
      "direct message",
      "message someone",
      "talk",
      "instant message",
      "text a coworker",
    ],
  },
  {
    id: "branch-company-chat",
    category: "Messages",
    question: "What are the Company-wide and branch channels in Messages?",
    answer: [
      "The Channels section at the top of the Messages page has two group chats. Company-wide goes to everyone at Big C Lumber; your branch channel (for example “Granger branch”) goes to everyone assigned to your branch.",
      "You only see your own branch's channel — it's based on the branch on your account. If your branch channel is missing or wrong, your account's branch needs to be corrected; contact HelpDesk.",
    ],
    link: { label: "Open Messages", to: "/messages" },
    keywords: [
      "group chat",
      "branch chat",
      "company chat",
      "everyone",
      "announcement",
      "channel",
    ],
  },
  {
    id: "message-reactions",
    category: "Messages",
    question: "How do I react to a message with an emoji?",
    answer: [
      "Hover over the message and click the smiley-face button that appears beside it, then pick an emoji. Your reaction shows up under the message for everyone in the conversation.",
      "Clicking the same emoji again — either in the picker or on the reaction bubble under the message — removes your reaction. Hover over a reaction bubble to see who reacted.",
    ],
    keywords: [
      "react",
      "reaction",
      "emoji",
      "like",
      "thumbs up",
      "heart",
      "remove reaction",
    ],
  },
  {
    id: "chat-send-photo",
    category: "Messages",
    question: "Can I send a photo in chat?",
    answer: [
      "Yes — click the image button to the left of the message box, pick a picture, and send. The photo is resized automatically so it sends quickly, and you can add text alongside it or send it on its own.",
      "Click any photo in a conversation to view it full size.",
    ],
    keywords: [
      "photo",
      "picture",
      "image",
      "attach",
      "attachment",
      "screenshot",
      "send a pic",
    ],
  },

  // ---------------------------------------------------------------- Safety
  {
    id: "toolbox-talks",
    category: "Safety",
    question: "Where can I watch the safety toolbox talk videos?",
    answer: [
      "Open the menu and go to Safety → Toolbox Talks. Every talk the safety team has posted is listed there, newest first — click play on any video to watch it right on the page.",
      "These are the same toolbox talks as in the mobile app, so you can watch on whichever is handier.",
    ],
    steps: [
      "Click the menu icon in the top-left corner.",
      "Open the Safety section and click Toolbox Talks.",
      "Press play on the talk you want to watch.",
    ],
    link: { label: "Open Toolbox Talks", to: "/safety/toolbox-talks" },
    keywords: [
      "safety",
      "toolbox",
      "talk",
      "video",
      "training",
      "monthly",
      "watch",
    ],
  },
  {
    id: "safety-overview",
    category: "Safety",
    question: "Where can I see what needs attention in safety across every branch?",
    answer: [
      "Open the menu and go to Safety → Safety Overview. It has one card for each safety area — Safety Concerns, Fire Extinguishers, Training Videos, and Toolbox Talk Results — showing the number that needs attention (open concerns, extinguishers due, overdue training, and how many people finished the latest toolbox talk). Click a card to open that area's page.",
      "The By branch table below shows the same numbers for each branch. Click a number to open that area's page already filtered to the branch.",
    ],
    link: { label: "Open Safety Overview", to: "/safety/overview" },
    note: "The Safety Overview and the Safety Concerns, Fire Extinguishers, Training Videos, and Toolbox Talk Results pages are limited to the Admin and Safety Manager roles.",
    keywords: [
      "safety manager",
      "safety dashboard",
      "branch",
      "overdue",
      "needs attention",
      "scorecard",
    ],
  },
  {
    id: "safety-concerns-review",
    category: "Safety",
    question: "Where do I see the safety concerns people have reported?",
    answer: [
      "Go to Safety → Safety Concerns. It lists every concern reported from the Big C mobile app, newest first. The tiles at the top filter the list — Needs attention (open or in review), Open, In review, Resolved, or All — and you can search or pick a branch.",
      "Click a concern to read the whole report and see its photos and notes.",
    ],
    link: { label: "Open Safety Concerns", to: "/safety/concerns" },
    note: "The website is view-only for concerns. To change a concern's status or add a note, use Safety Manager → Safety Submissions in the mobile app.",
    keywords: [
      "safety concern",
      "safety submission",
      "hazard",
      "report",
      "unsafe",
      "photos",
      "resolved",
    ],
  },
  {
    id: "fire-extinguisher-status",
    category: "Safety",
    question: "How do I see which fire extinguishers are due for inspection?",
    answer: [
      "Go to Safety → Fire Extinguishers. Every extinguisher is listed with its branch, location, and when it was last inspected. An extinguisher is due 30 days after its last inspection, or right away if it has never been inspected.",
      "Use the tiles at the top to show only the ones that Need attention, Failed their last check, are Due, were Never inspected, or are Up to date. Click an extinguisher to see its reference photo, open its map pin in Google Maps, and read its inspection history, including which checks failed.",
    ],
    link: { label: "Open Fire Extinguishers", to: "/safety/extinguishers" },
    note: "Extinguishers are added and inspected in the Big C mobile app (Safety & Training → Fire Extinguishers; managers can also use Safety Manager → Fire Extinguishers). The website shows them but can't change them.",
    keywords: [
      "fire extinguisher",
      "extinguisher",
      "monthly inspection",
      "inspection due",
      "never inspected",
      "failed",
    ],
  },
  {
    id: "upload-safety-video",
    category: "Safety",
    question: "How do I upload a toolbox talk or training video?",
    answer: [
      "For a toolbox talk, go to Safety → Toolbox Talk Results and click Upload toolbox talk. It shows up for everyone right away in Toolbox Talks, on the website and in the mobile app.",
      "For a training video, go to Safety → Training Videos and click Upload training video. A training video only shows up for the people it's assigned to — when the upload finishes, click Assign it now (see “How do I assign a training video?”).",
      "Videos must be MP4 files, up to 500 MB. Keep the page open until the upload finishes; a progress bar shows how far along it is.",
    ],
    steps: [
      "Go to Safety → Toolbox Talk Results (for a toolbox talk) or Safety → Training Videos (for a training video).",
      "Click Upload toolbox talk or Upload training video.",
      "Click Choose a video and pick the MP4 file.",
      "Check the title, add a description if you like, and click Upload.",
    ],
    link: { label: "Open Training Videos", to: "/safety/training" },
    note: "New videos start without a quiz. To add one, use Safety Manager → Toolbox Talks or Safety Manager → Training Videos in the mobile app.",
    keywords: [
      "upload",
      "add video",
      "post video",
      "new toolbox talk",
      "training video",
      "mp4",
      "safety video",
    ],
  },
  {
    id: "assign-training-video",
    category: "Safety",
    question: "How do I assign a training video to people or roles?",
    answer: [
      "Go to Safety → Training Videos, click the video, then click Assign. Pick one or more roles (each shows how many people have it), search for people by name, email, or branch, and choose a due date — No due date, 3 days, 1 week, 2 weeks, 30 days, or Pick a date. Then click Assign.",
      "Everyone newly assigned gets a notification on the website (the bell at the top) and, if they use the Big C mobile app, on their phone. They watch the video in the app under Safety & Training → Training Videos. If you pick a role, anyone who gets that role later — like a new hire — is assigned automatically. The app reminds people each day starting 2 days before the due date until they finish.",
      "The video's page shows everyone assigned, how they were assigned, their due date, and whether they're done or overdue. Use the tiles to show only Not done, Overdue, or Done.",
    ],
    steps: [
      "Go to Safety → Training Videos and click the video.",
      "Click Assign.",
      "Pick roles and/or search for people.",
      "Choose a due date and click Assign.",
    ],
    link: { label: "Open Training Videos", to: "/safety/training" },
    note: "Assigning a video again to someone who already has it updates their due date but never resets a completion. Admin isn't offered as a role, the same as in the mobile app.",
    keywords: [
      "assign",
      "assigned training",
      "due date",
      "role",
      "new hire",
      "required training",
      "overdue",
    ],
  },
  {
    id: "unassign-training-video",
    category: "Safety",
    question: "How do I take someone off a training video, or stop assigning it to a role?",
    answer: [
      "Open the video from Safety → Training Videos. To take one person off, click the remove-person button at the end of their row and confirm with Unassign — they won't see the video anymore, and their completion record is removed with it.",
      "If they were assigned through a role that's still auto-assigned, the mobile app will assign it to them again within a day. To stop that, click the × next to the role under Auto-assigned roles and confirm with Stop assigning. New people who get that role won't be assigned the video; everyone who already has it keeps it until you unassign them.",
    ],
    link: { label: "Open Training Videos", to: "/safety/training" },
    keywords: [
      "unassign",
      "remove",
      "take off",
      "stop assigning",
      "role",
      "wrong person",
    ],
  },
  {
    id: "toolbox-talk-results",
    category: "Safety",
    question: "How do I see who has finished a toolbox talk?",
    answer: [
      "Go to Safety → Toolbox Talk Results. Each talk shows how many people are done, quiz results, and how many started watching but didn't finish. Done means they passed the talk's quiz — or, for a talk with no quiz, tapped Mark as Present — in the Big C mobile app. Pick a branch to see just that branch.",
      "Click a talk to see the names: who's done (and their quiz score), who didn't pass the quiz, and who started watching but didn't finish.",
    ],
    link: { label: "Open Toolbox Talk Results", to: "/safety/toolbox-results" },
    keywords: [
      "toolbox",
      "who watched",
      "completion",
      "attendance",
      "quiz score",
      "mark as present",
      "finished",
    ],
  },

  // ---------------------------------------------------------------- Still stuck
  {
    id: "contact-helpdesk",
    category: "Getting Started",
    question: "None of this answered my question — who do I contact?",
    answer: [
      "Email helpdesk@bigclumber.com, or use the Submit a Ticket button at the top of this page, which opens a pre-addressed email.",
      "Include the page you were on, what you expected, and what happened instead. A screenshot saves a round trip.",
    ],
    keywords: [
      "help",
      "support",
      "ticket",
      "contact",
      "stuck",
      "broken",
      "it",
      "helpdesk",
    ],
  },
];

// Display order for the filter chips. Categories are intersected with what
// the data actually contains, so adding a name here without writing entries
// can't produce a chip that filters down to nothing.
const CATEGORY_ORDER = [
  "Getting Started",
  "Account & Access",
  "Messages",
  "Forms & Requests",
  "Exterior Orders",
  "Window & Door",
  "Web Leads",
  "Cabinet Quotes",
  "Fleet",
  "Safety",
  "Dashboard & Tools",
];

const usedCategories = new Set(faqs.map((faq) => faq.category));

export const faqCategories = [
  ...CATEGORY_ORDER.filter((cat) => usedCategories.has(cat)),
  // Anything that got a category but never made it into CATEGORY_ORDER still
  // gets a chip rather than silently becoming unreachable by filter.
  ...[...usedCategories].filter((cat) => !CATEGORY_ORDER.includes(cat)),
];
